import { defineStore } from 'pinia'
import { ref } from 'vue'
import { meApi } from '@/api/me'
import type { MenuNode } from '@/types/api'

/**
 * 动态菜单（附录C §2）：唯一数据源是 GET /api/me/v1/me/menus?clientId=。
 * 服务端已按角色过滤，前端不做二次裁剪，也不内置任何兜底菜单。
 *
 * 传入的 clientId 是**控制台自身所属应用**（appStore.consoleClientId，见 stores/app.ts）：
 * 菜单是导航结构，属于控制台，切换"管理对象"时不得跟着变。
 */
export const useMenuStore = defineStore('menu', () => {
  const tree = ref<MenuNode[]>([])
  /** 当前菜单所属应用（client_id）：切换应用必须重拉取，否则看到的是上一个应用的导航结构。 */
  const loadedClientId = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function load(clientId: string, force = false): Promise<void> {
    if (!force && loadedClientId.value === clientId && tree.value.length > 0) {
      return
    }
    loading.value = true
    error.value = null
    try {
      tree.value = await meApi.menus(clientId)
      loadedClientId.value = clientId
    } catch (e) {
      tree.value = [] // 失败不保留上一应用的菜单：宁可空导航，也不要把 A 的菜单当成 B 的
      loadedClientId.value = null
      error.value = e instanceof Error ? e.message : '菜单加载失败'
    } finally {
      loading.value = false
    }
  }

  /** 登出清理：与权限同批重置，避免下一次登录先闪出上一个账号的导航结构。 */
  function reset(): void {
    tree.value = []
    loadedClientId.value = null
    error.value = null
  }

  return { tree, loadedClientId, loading, error, load, reset }
})
